/**
 * Best-effort delivery of captured marketing attribution to the backend after
 * a successful Firebase sign-in. Calls the `recordSignupAttribution` callable
 * (auth is attached automatically by the Firebase Functions SDK). The Cloud
 * Function persists it so the owner signup-notification email can include it.
 *
 * Never throws and never blocks auth: attribution is purely informational.
 * `firebase/functions` is imported lazily so it stays out of the auth path's
 * critical bundle.
 */
import { getSignupAttributionPayload } from './attribution';
import { app } from './firebase';

// Marketing-only sends are deduped per uid. Identity-bearing sends (the
// salutation+first+last name the teacher typed at signup) are tracked
// separately so the no-identity `onAuthStateChanged` send can never block the
// identity send that the signup flow fires right after `updateProfile`.
let sentForUid: string | null = null;
let sentIdentityForUid: string | null = null;

export interface SignupIdentityPayload {
  signupDisplayName?: string | null;
  signupSalutation?: string | null;
  signupFirstName?: string | null;
  signupLastName?: string | null;
}

function cleanIdentityValue(value: string | null | undefined): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim().replace(/\s+/g, ' ');
  return trimmed ? trimmed.slice(0, 120) : null;
}

export async function sendSignupAttribution(
  uid: string | null | undefined,
  identity?: SignupIdentityPayload
): Promise<void> {
  if (!uid) return;

  const cleanedIdentity: SignupIdentityPayload = {
    signupDisplayName: cleanIdentityValue(identity?.signupDisplayName),
    signupSalutation: cleanIdentityValue(identity?.signupSalutation),
    signupFirstName: cleanIdentityValue(identity?.signupFirstName),
    signupLastName: cleanIdentityValue(identity?.signupLastName),
  };
  const hasIdentity = Object.values(cleanedIdentity).some(Boolean);

  // Allow an identity-bearing send through even when a marketing-only send for
  // this uid already happened; otherwise dedupe normally.
  if (hasIdentity) {
    if (sentIdentityForUid === uid) return;
  } else if (sentForUid === uid) {
    return;
  }

  // Mark optimistically so re-renders / token refreshes don't re-send.
  if (hasIdentity) sentIdentityForUid = uid;
  sentForUid = uid;

  const attribution = getSignupAttributionPayload();
  const payload =
    attribution || hasIdentity ? { ...(attribution ?? {}), ...cleanedIdentity } : null;
  if (!payload) return;

  try {
    const { getFunctions, httpsCallable } = await import('firebase/functions');
    const callable = httpsCallable(getFunctions(app), 'recordSignupAttribution');
    await callable(payload);
  } catch {
    // Best-effort only — allow a retry on the next sign-in by clearing guards.
    sentForUid = null;
    if (hasIdentity) sentIdentityForUid = null;
  }
}
